import {Observer, observer} from "mobx-react";
import React from "react";

import {bindInput} from "../../../utils/bind-input";
import {SaveButton} from "../../atoms/buttons/buttons";
import {
    ActionsColumn,
    HeaderGrid,
    InputWidthWrapper,
    PageContainer,
} from "../../atoms/layouts/category-layout-containers";
import {FormField} from "../../molecules/inputs/form-field";
import DatabaseRecordRow from "../../molecules/tables/database-record-row";
import {AdminBreadcrumb} from "../../organisms/admin-breadcrumb/admin-breadcrumb";
import {TemplateOptionsDrawer} from "../../organisms/drawers/template-option-drawer";
import {TableLayout} from "../../organisms/table-layout/table-layout";
import {AppLayout} from "../../templates/app-layout/app-layout";
import {DatabaseTemplateRecordEditPageStore} from "./database-template-record-edit-store";

// Props 型定義
type Props = {pageStore: DatabaseTemplateRecordEditPageStore};

// 1) ヘッダー部：保存ボタンのみを observer で監視
const RecordEditHeader = observer(({store}: {store: DatabaseTemplateRecordEditPageStore}) => (
    <HeaderGrid>
        <InputWidthWrapper width={735}>
            <FormField
                label="データラベル"
                value={store.templateDataLabel}
                errorText={store.templateDataLabelError}
                placeholder="データラベル"
                required
                {...bindInput(store.setTemplateDataLabel)}
            />
        </InputWidthWrapper>
        <ActionsColumn>
            <SaveButton disabled={!store.canUpdate} onClick={store.updateTemplateRecord}>
                保存
            </SaveButton>
        </ActionsColumn>
    </HeaderGrid>
));

// 2) テーブル部：各行を監視（DatabaseRecordRow は内部で observer されている）
const RecordEditTable = observer(({store}: {store: DatabaseTemplateRecordEditPageStore}) => (
    <TableLayout areaLabel="データの編集" columns={store.DEFAULT_COLUMN_DEFS}>
        {store.templateColumnList.map((row) => (
            <DatabaseRecordRow
                key={row.id}
                row={row}
                values={store.values}
                setValue={store.setValue}
                onImageUploaded={store.onImageUploaded}
                openOptions={store.openOptions}
            />
        ))}
    </TableLayout>
));

// 3) ドロワー部：個別 props を Observer でラップ
const RecordEditDrawer = ({store}: {store: DatabaseTemplateRecordEditPageStore}) => (
    <Observer>
        {() => (
            <TemplateOptionsDrawer
                isOpen={store.isDrawerOpen}
                onClose={store.closeOptions}
                optionFields={store.optionFields}
                selectedRowLabel={store.selectedRow?.label ?? ""}
                selectedOptions={store.selectedOptions}
                selectedId={store.selectedId}
                setOption={store.setOption}
            />
        )}
    </Observer>
);

// 4) メインコンポーネント：observer を外し、必要な部分だけ再レンダー
const DatabaseTemplateRecordEditPage: React.FC<Props> = ({pageStore}) => (
    <AppLayout
        breadcrumb={
            <AdminBreadcrumb
                factory={(builder) =>
                    builder
                        .databaseTemplates()
                        .databaseTemplateDetail(pageStore.templateName, pageStore.templateId)
                        .custom(`${pageStore.templateDataLabel} の編集`)
                        .build()
                }
            />
        }
    >
        <PageContainer>
            <RecordEditHeader store={pageStore} />
            <RecordEditTable store={pageStore} />
            <RecordEditDrawer store={pageStore} />
        </PageContainer>
    </AppLayout>
);

export default DatabaseTemplateRecordEditPage;
